import React, { useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { toast } from "sonner";

/** Public Links — the shareable URL for a profile.
 * `/@handle` when the profile has a handle (stable, redirects to the canonical page),
 * otherwise the canonical path (`/p/{slug}`, `/b/{slug}`, `/capital/{slug}`). */
export function publicProfileUrl({ handle, slug, basePath = "/p" }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return handle ? `${origin}/@${handle}` : `${origin}${basePath}/${slug}`;
}

/** `isOwner` + `publicLinkEnabled` come from the server. Only the owner sees the toggle;
 * nobody can change another member's setting from here (the PUT is owner-scoped anyway).
 * `updatePath` is the owner-scoped endpoint that stores the flag — `/profile/me` for a
 * personal profile, `/orgs/{kind}/{id}` for a Business or Capital profile. */
export function ShareProfileButton({ handle, slug, name, isOwner = false,
                                     publicLinkEnabled = false, basePath = "/p",
                                     updatePath = "/profile/me", subject = "profile",
                                     testId = "profile-share-button" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [enabled, setEnabled] = useState(publicLinkEnabled === true);
  const [saving, setSaving] = useState(false);
  const url = publicProfileUrl({ handle, slug, basePath });
  const showLink = !isOwner || enabled;

  const toggle = async (next) => {
    setSaving(true);
    const prior = enabled;
    setEnabled(next);
    try {
      await api.put(updatePath, { public_link_enabled: next });
      toast.success(next ? "Public link is on" : "Public link is off");
    } catch {
      setEnabled(prior);
      toast.error("Couldn't update your public link. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: name || "Startup Otter profile", url });
      setOpen(false);
    } catch {
      /* dismissed — leave the popover open so Copy Link is still there */
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 text-primary hover:bg-primary/10 hover:text-primary"
                aria-label="Share this profile" title="Share this profile" data-testid={testId}>
          <Share2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-4" data-testid="profile-share-popover">
        <div className="text-sm font-[620] text-foreground">Share this {subject}</div>

        {isOwner && (
          <div className="mt-3 flex items-start justify-between gap-3 rounded-md bg-secondary px-3 py-2.5">
            <label htmlFor="public-link-toggle" className="text-xs font-[560] text-foreground">
              Enable public link
            </label>
            <Switch id="public-link-toggle" checked={enabled} disabled={saving}
                    onCheckedChange={toggle} data-testid="public-link-toggle" />
          </div>
        )}

        <p className="mt-2 text-xs text-muted-foreground" data-testid="profile-share-note">
          {isOwner
            ? (enabled
                ? `Anyone with this link can view this ${subject} — no Startup Otter account needed. Turn it off at any time and the link stops working.`
                : `This ${subject} is members-only right now. Turn this on to let anyone — an investor, a partner, a client — open it without an account.`)
            : `Copy the link to this ${subject}.`}
        </p>

        {showLink && (
          <>
            <div className="mt-3 break-all rounded-md bg-secondary px-3 py-2 text-xs text-foreground" data-testid="profile-share-url">
              {url}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" className="rounded-full" onClick={copy} data-testid="profile-share-copy">
                {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
                {copied ? "Link copied" : "Copy link"}
              </Button>
              {typeof navigator !== "undefined" && navigator.share && (
                <Button size="sm" variant="secondary" className="rounded-full" onClick={nativeShare} data-testid="profile-share-native">
                  Share…
                </Button>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
